class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  isDesktopDrawerMode() {
    return window.matchMedia && window.matchMedia('(min-width: 990px)').matches;
  }

  enableDesktopDrawerMode() {
    document.documentElement.classList.add('led-cart-desktop-drawer-open');
    document.body.classList.add('led-cart-desktop-drawer-open');
  }

  disableDesktopDrawerMode() {
    document.documentElement.classList.remove('led-cart-desktop-drawer-open');
    document.body.classList.remove('led-cart-desktop-drawer-open');
  }

  lockPageScroll() {
    if (this._ledScrollLocked) return;

    this._ledScrollLocked = true;
    this._ledScrollY = window.scrollY || document.documentElement.scrollTop || 0;

    // Keep the page in place without using position: fixed.
    // position: fixed was the reason desktop wheel scrolling felt jumpy/not seamless.
    this._ledOriginalHtmlStyleAttribute = document.documentElement.getAttribute('style');
    this._ledOriginalBodyStyleAttribute = document.body.getAttribute('style');

    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    document.documentElement.classList.add('led-cart-drawer-open');
    document.body.classList.add('overflow-hidden', 'led-cart-drawer-open');

    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow-y', 'hidden', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow-y', 'hidden', 'important');

    // Prevent layout jump when the browser scrollbar disappears.
    if (scrollbarWidth > 0) {
      document.body.style.setProperty('padding-right', `${scrollbarWidth}px`, 'important');
    }
  }

  unlockPageScroll() {
    if (!this._ledScrollLocked) return;

    document.documentElement.classList.remove('led-cart-drawer-open');
    document.body.classList.remove('overflow-hidden', 'led-cart-drawer-open');

    if (this._ledOriginalHtmlStyleAttribute === null) {
      document.documentElement.removeAttribute('style');
    } else {
      document.documentElement.setAttribute('style', this._ledOriginalHtmlStyleAttribute);
    }

    if (this._ledOriginalBodyStyleAttribute === null) {
      document.body.removeAttribute('style');
    } else {
      document.body.setAttribute('style', this._ledOriginalBodyStyleAttribute);
    }

    window.scrollTo(0, this._ledScrollY || 0);
    this._ledScrollLocked = false;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (this.isDesktopDrawerMode()) {
      this.enableDesktopDrawerMode();
    } else {
      this.lockPageScroll();
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.disableDesktopDrawerMode();
    this.unlockPageScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async refresh(dontRefreshCartItems, items_count) {
    try {
        const response = this.getSectionsToRender().map(section => section.id);
        const ADDTOCART_OBJECT = {
            sections: await (await fetch(`?sections=${response.join(",")}`)).json()
        };
        this.renderContents(ADDTOCART_OBJECT),
        this.classList.contains("is-empty") && this.classList.remove("is-empty"),
        items_count <= 0 && (this.classList.contains("is-empty") || this.classList.add("is-empty"))
    } catch (error) {
        console.log(error),
        this.dispatchEvent(new CustomEvent("on:cart:error",{
            bubbles: !0,
            detail: {
                error: this.errorMsg.textContent
            }
        }))
    }
  }

}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
